/**
 * The canonical order of a project's audio files.
 *
 * Chaptered takes first, in their chapter's `order_index`; anything uploaded
 * but not yet mapped keeps upload order behind them. A file is identified by
 * `audio_filename` throughout — a chapter's name is heading-derived and can go
 * stale, so it is never the key.
 *
 * The server gives a file its `place`: where the list was last sorted or
 * dragged to (proofed or not - so a sort holds for every file), else its
 * chapter's. A place wins over the chapter's own order, which a later re-map
 * resets to the book's. A corrected delivery stands where the file it replaced
 * stood - it takes that file's place even before (or without) a chapter of its
 * own - and unplaced files are listed by their first delivery, so a
 * replacement never drops to the bottom of the list.
 *
 * This lives here because two screens now depend on agreeing: the project's
 * takes list, and the phone's listening queue. A queue that played takes in a
 * different order from the list that produced them would be its own bug, and
 * the duplication is exactly how that happens.
 */

export interface OrderChapter {
  audio_filename?: string | null;
  order_index: number;
}

export interface OrderFile {
  filename: string;
  place?: number | null;
}

export function orderTakes(
  chapters: OrderChapter[],
  files: (string | OrderFile)[],
): string[] {
  const placeOf = new Map<string, number>();
  for (const f of files) if (typeof f !== "string" && f.place != null) placeOf.set(f.filename, f.place);
  const placed: { fn: string; at: number }[] = chapters
    .filter((c) => c.audio_filename)
    .map((c) => ({ fn: c.audio_filename as string, at: placeOf.get(c.audio_filename as string) ?? c.order_index }));
  const claimed = new Set(placed.map((p) => p.fn));
  const extra: string[] = [];
  for (const f of files) {
    const fn = typeof f === "string" ? f : f.filename;
    if (claimed.has(fn)) continue;
    const at = placeOf.get(fn);
    if (at != null) placed.push({ fn, at });
    else extra.push(fn);
  }
  placed.sort((a, b) => a.at - b.at);   // stable: a chapter before a file sharing its place
  const seen = new Set<string>();
  return [...placed.map((p) => p.fn), ...extra].filter((fn) => (seen.has(fn) ? false : (seen.add(fn), true)));
}
